/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Models
import { ApiResponse, PriceConfigExtended, CLIENT_PLAN_TYPES, PRICE_CONFIGURATION_TYPE } from '@core/models';
import { PRICE_CONFIGURATION_GROUP_NAME } from '@new-client/enums/price-configuration-group-name.enum';
// Rxjs
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
// Services
import { ApiService } from 'app/services/api.service';

@Injectable({
  providedIn: 'root',
})
export class PriceConfigService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  static getTypeTitle(type: string): string {
    switch (type) {
      case PRICE_CONFIGURATION_TYPE.USER_COST:
        return PRICE_CONFIGURATION_GROUP_NAME.USERS;

      case PRICE_CONFIGURATION_TYPE.NO_STANDARD_LOADS:
        return PRICE_CONFIGURATION_GROUP_NAME.SHIPMENT;

      case PRICE_CONFIGURATION_TYPE.ZUUM_TEXT_ADD_ON:
        return PRICE_CONFIGURATION_GROUP_NAME.ADD_ON_FEATURE;

      case PRICE_CONFIGURATION_TYPE.TOTAL_REFERRALS_LIMIT:
        return PRICE_CONFIGURATION_GROUP_NAME.ADD_ON_FEATURE;

      case PRICE_CONFIGURATION_TYPE.UI_UX_DESIGN:
        return PRICE_CONFIGURATION_GROUP_NAME.PROJECT_TYPE_FEE;

      case PRICE_CONFIGURATION_TYPE.SAAS_INVOICE_PAYMENT_TERMS:
        return PRICE_CONFIGURATION_GROUP_NAME.SASS_INVOICE;

      case PRICE_CONFIGURATION_TYPE.CUSTOM_INVOICE_PAYMENT_TERMS:
        return PRICE_CONFIGURATION_GROUP_NAME.CUSTOM_INVOICE;

      case PRICE_CONFIGURATION_TYPE.WAIVE_PLATFORM_ACCESS_FEE:
        return PRICE_CONFIGURATION_GROUP_NAME.WAIVER;

      case PRICE_CONFIGURATION_TYPE.PER_SEAT:
        return PRICE_CONFIGURATION_GROUP_NAME.PLATFORM_ACCESS;

      case PRICE_CONFIGURATION_TYPE.DYNAMIC_SMART_RATE:
        return PRICE_CONFIGURATION_GROUP_NAME.FEATURES;

      case PRICE_CONFIGURATION_TYPE.GENERAL_INSURANCE:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_INSURANCE;

      case PRICE_CONFIGURATION_TYPE.MC_DOT_CHECKED:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_CARRIER_VALIDATION_OK_TO_LOAD;

      case PRICE_CONFIGURATION_TYPE.DRIVER_CURRENT_LOCATION:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_CARRIER_TENDER_OPTIONS;

      case PRICE_CONFIGURATION_TYPE.FOUR_CHECK_CALLS:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_UPDATING_STATUSES_WITHOUT_CHECK_CALLS;

      case PRICE_CONFIGURATION_TYPE.LOCATION_MUST_HAVE_NAMES:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_LOCATION_NAME;

      case PRICE_CONFIGURATION_TYPE.ASSIGN_SALES_PERSON_SHIPMENT:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_SALES_PERSON_REQUIRED;

      case PRICE_CONFIGURATION_TYPE.MUST_DEFINE_AWARD_TYPE:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_AWARD_TYPE_REQUIRED;

      case PRICE_CONFIGURATION_TYPE.MUST_ENTER_COMPANY_INFO:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_CUSTOMER_INFO_REQUIRED;

      case PRICE_CONFIGURATION_TYPE.QUICKBOOKS_SEND_INVOICE_BEFORE_EXPORT:
        return PRICE_CONFIGURATION_GROUP_NAME.RS_QUICKBOOK_INTEGRATION;

      case PRICE_CONFIGURATION_TYPE.COMPANY_CONFIG:
        return PRICE_CONFIGURATION_GROUP_NAME.COMPANY_CONFIG;

      case PRICE_CONFIGURATION_TYPE.EQUIPMENT_TYPE:
        return PRICE_CONFIGURATION_GROUP_NAME.EQUIPMENT_TYPE;
      case PRICE_CONFIGURATION_TYPE.SHIPMENT_OPTIONS:
        return PRICE_CONFIGURATION_GROUP_NAME.SHIPMENT_OPTIONS;
      case PRICE_CONFIGURATION_TYPE.CUSTOMERS_FOR_SHIPMENTS:
        return PRICE_CONFIGURATION_GROUP_NAME.CUSTOMERS_FOR_SHIPMENTS;
      case PRICE_CONFIGURATION_TYPE.ACCOUNTING:
        return PRICE_CONFIGURATION_GROUP_NAME.ACCCOUNTING;
      default:
        return type;
    }
  }

  static getGroupAdditionalText(groupName: string): string {
    switch (groupName) {
      case PRICE_CONFIGURATION_GROUP_NAME.RS_CARRIER_VALIDATION_OK_TO_LOAD:
        return 'Require your team to verify carriers in order to tender them loads.';
      case PRICE_CONFIGURATION_GROUP_NAME.RS_CARRIER_TENDER_OPTIONS:
        return 'Require your team to input driver and equipment information when tendering a load to a carrier';
      default:
        return '';
    }
  }

  getConfig(clientId: number, name: string): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi}/price-config/${clientId}/${name}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getActivePriceConfigByClientId(clientId: number): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi}/price-config/${clientId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateCompanyConfig(clientId: number, config: PriceConfigExtended): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/price-config/${clientId}`, config)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateBatchPriceConfigurations(
    clientId: number,
    configurations: PriceConfigExtended[]
  ): Observable<ApiResponse<PriceConfigExtended[]>> {
    return this.http
      .put<ApiResponse>(`${this.apiService.superAdminApi}/price-config/batch-update/${clientId}`, configurations)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  addCompanyConfig(clientId: number, config: PriceConfigExtended): Observable<ApiResponse> {
    delete config.adjustedValue;
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/price-config/new-config/for-client/${clientId}`, {
        ...config,
        client: { id: clientId },
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }
}
